import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  FaUsers,
  FaMapMarkerAlt,
  FaComments,
  FaChartBar,
  FaHistory,
  FaArrowUp,
  FaArrowDown,
  FaExclamationTriangle,
  FaSpinner,
  FaCheckCircle,
  FaClock,
  FaBullhorn,
  FaUserCheck,
  FaEnvelope,
  FaEye,
  FaPercentage
} from "react-icons/fa";

const SenatorialDistrictDashboard = () => {
  const { user, token } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || "https://api.tinubu-ambode.org";
      const apiUrl = `${apiBaseUrl}/api/v1/analytics/dashboard`;
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      const authToken = token || localStorage.getItem('token') || localStorage.getItem('authToken');
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || "API request failed");
      }
      
      setDashboardData(result.data);
      setLastUpdated(new Date().toLocaleTimeString());
      
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const container = {
    width: "100%",
    minHeight: "100vh",
    fontFamily: "Inter, sans-serif",
    backgroundColor: "#f8fafc",
    padding: "0",
    margin: "0",
  };

  const contentWrapper = {
    width: "100%",
    maxWidth: "100%",
    padding: "15px",
    boxSizing: "border-box",
  };

  const header = {
    marginBottom: "25px",
    padding: "0 5px",
  };

  const title = {
    fontSize: "22px",
    fontWeight: "700",
    color: "#2563eb",
    marginBottom: "8px",
    lineHeight: "1.3",
  };

  const subtitle = {
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.5",
    margin: "0",
  };

  const statGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "15px",
    marginBottom: "25px",
    width: "100%",
  };

  const statCard = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    padding: "20px",
    border: "1px solid #e5e7eb",
    width: "100%",
    boxSizing: "border-box",
    minHeight: "140px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "transform 0.2s, box-shadow 0.2s",
  };

  const statTitle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#2563eb",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "10px",
  };

  const statValue = {
    fontSize: "24px",
    fontWeight: "700",
    color: "#111827",
    marginBottom: "6px",
  };

  const statSubValue = {
    fontSize: "14px",
    color: "#6b7280",
    fontWeight: "500",
  };

  const mainGrid = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    width: "100%",
  };

  const card = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    width: "100%",
    boxSizing: "border-box",
  };

  const sectionTitle = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "18px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "12px",
    borderBottom: "2px solid #f1f5f9",
  };

  const sectionIcon = {
    color: "#2563eb",
    fontSize: "16px",
  };

  const distributionItem = {
    borderBottom: "1px solid #f8fafc",
    padding: "16px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const distributionName = {
    fontSize: "15px",
    fontWeight: "600",
    color: "#111827",
  };

  const distributionCount = {
    fontSize: "16px",
    fontWeight: "700",
    color: "#3b82f6",
  };

  const statusItem = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #f8fafc",
  };

  const statusLabel = {
    fontSize: "14px",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const statusValue = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
  };

  const engagementMetric = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid #f8fafc",
  };

  const engagementLabel = {
    fontSize: "14px",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const engagementValue = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
  };

  const activityItem = {
    display: "flex",
    gap: "12px",
    borderBottom: "1px solid #f8fafc",
    padding: "16px 0",
    alignItems: "flex-start",
  };

  const activityIcon = {
    width: "8px",
    height: "8px",
    backgroundColor: "#2563eb",
    borderRadius: "50%",
    marginTop: "8px",
    flexShrink: 0,
  };

  const activityDetails = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  };

  const activityTitle = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
    lineHeight: "1.4",
  };

  const activitySub = {
    fontSize: "13px",
    color: "#3b82f6",
    fontWeight: "500",
  };

  const activityTime = {
    fontSize: "12px",
    color: "#9ca3af",
    alignSelf: "flex-start",
    backgroundColor: "#f8fafc",
    padding: "4px 8px",
    borderRadius: "6px",
    marginTop: "4px",
  };

  const loadingContainer = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    fontSize: "16px",
    color: "#6b7280",
    gap: "15px",
  };

  const errorContainer = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    padding: "20px",
    textAlign: "center",
    gap: "15px",
  };

  const errorIcon = {
    fontSize: "48px",
    color: "#ef4444",
  };

  const errorText = {
    color: "#ef4444",
    fontSize: "16px",
    maxWidth: "500px",
    lineHeight: "1.5",
  };

  const retryButton = {
    backgroundColor: "#2563eb",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return "0";
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const formatPercentage = (num) => {
    if (num === undefined || num === null) return "0%";
    return `${num.toFixed(2)}%`;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#f59e0b",
      approved: "#10b981",
      suspended: "#ef4444",
      rejected: "#6b7280"
    };
    return colors[status] || "#6b7280";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: FaClock,
      approved: FaCheckCircle,
      suspended: FaExclamationTriangle,
      rejected: FaExclamationTriangle
    };
    return icons[status] || FaUsers;
  };

  const getTrendIcon = (value) => {
    if (value > 0) return { icon: <FaArrowUp />, color: "#10b981" };
    if (value < 0) return { icon: <FaArrowDown />, color: "#ef4444" };
    return { icon: null, color: "#6b7280" };
  };

  const getGeographicLevelName = (level) => {
    const levelNames = {
      "ward_code": "Wards",
      "lga_code": "Local Governments",
      "fc_code": "Federal Constituencies",
      "state_code": "States",
      "sd_code": "Senatorial Districts"
    };
    return levelNames[level] || level;
  };

  const responsiveStyles = `
    @media (min-width: 768px) {
      .dashboard-content-wrapper {
        padding: 25px;
        max-width: 1200px;
        margin: 0 auto;
      }
      
      .dashboard-stat-grid {
        grid-template-columns: repeat(4, 1fr);
        gap: 20px;
        margin-bottom: 35px;
      }
      
      .dashboard-stat-card {
        padding: 25px;
        min-height: 160px;
      }
      
      .dashboard-stat-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      }
      
      .dashboard-stat-value {
        font-size: 28px;
      }
      
      .dashboard-main-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 25px;
      }
      
      .dashboard-card {
        padding: 25px;
      }
      
      .dashboard-activity-details {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
      
      .dashboard-activity-time {
        align-self: center;
        margin-top: 0;
      }
    }
    
    @media (max-width: 767px) {
      .dashboard-stat-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    
    @media (max-width: 480px) {
      .dashboard-stat-grid {
        grid-template-columns: 1fr;
      }
    }
  `;

  if (loading) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={loadingContainer}>
            <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '32px' }} />
            <div>Loading dashboard data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={errorContainer}>
            <FaExclamationTriangle style={errorIcon} />
            <div style={errorText}>{error}</div>
            <button 
              style={retryButton} 
              onClick={fetchDashboardData}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={container}>
        <div style={contentWrapper}>
          <div style={errorContainer}>
            <FaExclamationTriangle style={errorIcon} />
            <div style={errorText}>No data available</div>
            <button 
              style={retryButton} 
              onClick={fetchDashboardData}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { summary, recent_activity, geographic_distribution, engagement, metadata } = dashboardData;
  const jurisdiction = metadata?.jurisdiction || {};

  return (
    <>
      <style>{responsiveStyles}</style>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .stat-trend-positive {
          color: #10b981;
          font-weight: 600;
        }
        
        .stat-trend-negative {
          color: #ef4444;
          font-weight: 600;
        }
      `}</style>
      
      <div style={container} className="dashboard-container">
        <div style={contentWrapper} className="dashboard-content-wrapper">
          <div style={header}>
            <div>
              <h2 style={title} className="dashboard-title">Senatorial District Administration Dashboard</h2>
              <p style={subtitle} className="dashboard-subtitle">
                Welcome, {user?.full_name || 'Senatorial District Administrator'}! Manage your senatorial district operations
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
              <div>
                {jurisdiction.senatorial_district && (
                  <p style={{...subtitle, marginTop: '5px'}}>
                    <strong>Senatorial District:</strong> {jurisdiction.senatorial_district} ({jurisdiction.sd_code})
                  </p>
                )}
                {jurisdiction.state && (
                  <p style={{...subtitle, marginTop: '5px'}}>
                    <strong>State:</strong> {jurisdiction.state}
                  </p>
                )}
                {jurisdiction.federal_constituency && (
                  <p style={{...subtitle, marginTop: '5px'}}>
                    <strong>Federal Constituency:</strong> {jurisdiction.federal_constituency}
                  </p>
                )}
              </div>
              {lastUpdated && (
                <p style={{...subtitle, fontSize: '12px', color: '#9ca3af'}}>
                  Last updated: {lastUpdated}
                </p>
              )}
            </div>
          </div>

          <div style={statGrid} className="dashboard-stat-grid">
            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Total Members</span>
                <FaUsers color="#2563eb" />
              </div>
              <div style={statValue}>{formatNumber(summary?.total_members)}</div>
              <div style={statSubValue}>
                {formatNumber(summary?.approved_members)} approved • {formatPercentage(summary?.approval_rate)} approval rate
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Pending Approvals</span>
                <FaClock color="#f59e0b" />
              </div>
              <div style={statValue}>{formatNumber(summary?.pending_approvals)}</div>
              <div style={statSubValue}>
                {formatNumber(recent_activity?.new_registrations_last_30_days)} new last 30 days
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Active Users</span>
                <FaUserCheck color="#10b981" />
              </div>
              <div style={statValue}>{formatNumber(engagement?.active_users)}</div>
              <div style={statSubValue}>
                {formatNumber(engagement?.group_chats?.total_groups)} groups • {formatNumber(engagement?.messages?.total_messages)} messages
              </div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Announcements</span>
                <FaBullhorn color="#8b5cf6" />
              </div>
              <div style={statValue}>{formatNumber(engagement?.announcements?.total_created)}</div>
              <div style={statSubValue}>
                {formatPercentage(engagement?.announcements?.delivery_rate)} delivery • {formatPercentage(engagement?.announcements?.read_rate)} read
              </div>
            </div>
          </div>

          <div style={mainGrid} className="dashboard-main-grid">
            {/* Left Column - Geographic Distribution */}
            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaMapMarkerAlt style={sectionIcon} />
                {getGeographicLevelName(geographic_distribution?.level)} Distribution
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                  {geographic_distribution?.distribution?.length || 0} units
                </span>
              </h3>

              {geographic_distribution?.distribution?.map((item, index) => (
                <div key={index} style={distributionItem} className="dashboard-distribution-item">
                  <div style={distributionName}>{item.code}</div>
                  <div style={distributionCount}>{formatNumber(item.count)} members</div>
                </div>
              ))}

              {(!geographic_distribution?.distribution || geographic_distribution.distribution.length === 0) && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                  No geographic distribution data available
                </div>
              )}
            </div>

            {/* Right Column - Member Status Overview */}
            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaChartBar style={sectionIcon} />
                Member Status Overview
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                  Last 30 days
                </span>
              </h3>

              {recent_activity?.by_status ? (
                Object.entries(recent_activity.by_status).map(([status, count]) => {
                  const StatusIcon = getStatusIcon(status);
                  const trend = getTrendIcon(count);
                  return (
                    <div key={status} style={statusItem} className="dashboard-status-item">
                      <div style={statusLabel}>
                        <StatusIcon color={getStatusColor(status)} />
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={statusValue}>{formatNumber(count)}</div>
                        {trend.icon && (
                          <span style={{ color: trend.color }}>
                            {trend.icon}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                  No recent activity data available
                </div>
              )}
            </div>

            {/* Bottom Left - Engagement Metrics */}
            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaBullhorn style={sectionIcon} />
                Engagement Analytics
              </h3>

              <div style={engagementMetric}>
                <div style={engagementLabel}>
                  <FaEnvelope color="#3b82f6" />
                  Total Announcements
                </div>
                <div style={engagementValue}>{formatNumber(engagement?.announcements?.total_created)}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>
                  <FaUsers color="#3b82f6" />
                  Total Recipients
                </div>
                <div style={engagementValue}>{formatNumber(engagement?.announcements?.total_recipients)}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>
                  <FaPercentage color="#10b981" />
                  Delivery Rate
                </div>
                <div style={engagementValue}>{formatPercentage(engagement?.announcements?.delivery_rate)}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>
                  <FaEye color="#8b5cf6" />
                  Read Rate
                </div>
                <div style={engagementValue}>{formatPercentage(engagement?.announcements?.read_rate)}</div>
              </div>

              <div style={engagementMetric}>
                <div style={engagementLabel}>
                  <FaComments color="#f59e0b" />
                  Total Messages
                </div>
                <div style={engagementValue}>{formatNumber(engagement?.messages?.total_messages)}</div>
              </div>
            </div>

            {/* Bottom Right - System Overview */}
            <div style={card} className="dashboard-card dashboard-activity-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaHistory style={sectionIcon} />
                System Overview
              </h3>

              <div style={activityItem} className="dashboard-activity-item">
                <div style={activityIcon}></div>
                <div style={activityDetails} className="dashboard-activity-details">
                  <div style={activityTitle}>Jurisdiction Level</div>
                  <div style={activitySub}>{jurisdiction.level || 'Senatorial District'}</div>
                  <div style={activityTime} className="dashboard-activity-time">System</div>
                </div>
              </div>

              {jurisdiction.senatorial_district && (
                <div style={activityItem} className="dashboard-activity-item">
                  <div style={activityIcon}></div>
                  <div style={activityDetails} className="dashboard-activity-details">
                    <div style={activityTitle}>Senatorial District</div>
                    <div style={activitySub}>{jurisdiction.senatorial_district}</div>
                    <div style={activityTime} className="dashboard-activity-time">{jurisdiction.sd_code || ''}</div>
                  </div>
                </div>
              )}

              <div style={activityItem} className="dashboard-activity-item">
                <div style={activityIcon}></div>
                <div style={activityDetails} className="dashboard-activity-details">
                  <div style={activityTitle}>State</div>
                  <div style={activitySub}>{jurisdiction.state || 'N/A'}</div>
                  <div style={activityTime} className="dashboard-activity-time">Location</div>
                </div>
              </div>

              <div style={activityItem} className="dashboard-activity-item">
                <div style={activityIcon}></div>
                <div style={activityDetails} className="dashboard-activity-details">
                  <div style={activityTitle}>Total Group Chats</div>
                  <div style={activitySub}>{formatNumber(engagement?.group_chats?.total_groups)} active groups</div>
                  <div style={activityTime} className="dashboard-activity-time">Communication</div>
                </div>
              </div>

              <div style={activityItem} className="dashboard-activity-item">
                <div style={activityIcon}></div>
                <div style={activityDetails} className="dashboard-activity-details">
                  <div style={activityTitle}>Data Generated</div>
                  <div style={activitySub}>
                    {metadata?.generated_at ? new Date(metadata.generated_at).toLocaleDateString() : 'Today'}
                  </div>
                  <div style={activityTime} className="dashboard-activity-time">
                    {metadata?.generated_at ? new Date(metadata.generated_at).toLocaleTimeString() : 'Now'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SenatorialDistrictDashboard;